// AWS WAF Captcha Manager
// Shows captcha modal when WAF returns 405, handles retry flow
// https://docs.aws.amazon.com/waf/latest/developerguide/waf-js-captcha-api.html

import config from 'config'
import debug, { logError } from 'util/debug'

const WAF_CAPTCHA_CONTAINER_ID = 'waf-captcha-container'
const WAF_CAPTCHA_TIMEOUT_MS = 5 * 60 * 1000 // 5 minutes

let captchaPromise = null
let timeoutId = null
let rejectFn = null

function handleEscapeKey(event) {
  if (event.key === 'Escape' && rejectFn) {
    debug('[WAF Captcha] Cancelled via Escape key')
    const reject = rejectFn
    cleanup()
    reject(new Error('Captcha cancelled by user'))
  }
}

function cleanup() {
  // Clear timeout
  if (timeoutId) {
    clearTimeout(timeoutId)
    timeoutId = null
  }

  // Remove escape key listener
  document.removeEventListener('keydown', handleEscapeKey)

  // Remove overlay
  const overlay = document.getElementById(WAF_CAPTCHA_CONTAINER_ID)
  if (overlay) {
    overlay.remove()
  }

  // Reset state
  captchaPromise = null
  rejectFn = null
}

function createOverlay(onClose) {
  const overlay = document.createElement('div')
  overlay.id = WAF_CAPTCHA_CONTAINER_ID
  overlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
  `

  // Create inner container for captcha widget
  const captchaContainer = document.createElement('div')
  captchaContainer.id = 'waf-captcha-widget'
  captchaContainer.style.cssText = `
    background: white;
    border-radius: 8px;
    padding: 24px;
    max-width: 400px;
    width: 90%;
    position: relative;
  `

  // Create close button
  const closeButton = document.createElement('button')
  closeButton.type = 'button'
  closeButton.setAttribute('aria-label', 'Close captcha')
  closeButton.style.cssText = `
    position: absolute;
    top: 8px;
    right: 8px;
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    cursor: pointer;
    font-size: 24px;
    line-height: 1;
    color: #666;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
  `
  closeButton.textContent = '×'
  closeButton.onmouseover = () => {
    closeButton.style.background = '#f0f0f0'
  }
  closeButton.onmouseout = () => {
    closeButton.style.background = 'transparent'
  }
  closeButton.onclick = onClose

  captchaContainer.appendChild(closeButton)
  overlay.appendChild(captchaContainer)

  document.body.appendChild(overlay)
  return captchaContainer
}

/**
 * Shows the AWS WAF captcha modal and returns a promise that resolves
 * when the user successfully completes the captcha.
 *
 * If a captcha is already being shown, returns the existing promise
 * to prevent multiple captchas from appearing.
 *
 * The captcha can be cancelled by:
 * - Clicking the close button
 * - Pressing Escape key
 * - Timeout after 5 minutes (configurable)
 *
 * @returns {Promise<void>} Resolves when captcha is solved, rejects on error/cancel
 */
export function showCaptcha() {
  debug('[WAF Captcha] showCaptcha called')

  // If captcha is already showing, return the existing promise
  // This handles the case of multiple simultaneous 405 responses
  if (captchaPromise) {
    debug('[WAF Captcha] Returning existing promise')
    return captchaPromise
  }

  captchaPromise = new Promise((resolve, reject) => {
    // Store reject function for external cancellation (escape key, close button, timeout)
    rejectFn = reject

    // Check if SDK is loaded
    if (!window.AwsWafCaptcha) {
      logError(
        '[WAF Captcha] SDK not loaded - window.AwsWafCaptcha is undefined'
      )
      cleanup()
      reject(new Error('AWS WAF Captcha SDK not loaded'))
      return
    }

    debug('[WAF Captcha] SDK available, creating overlay')

    // Create close handler that captures reject before cleanup clears it
    const handleClose = () => {
      debug('[WAF Captcha] Cancelled via close button')
      const currentReject = rejectFn
      cleanup()
      if (currentReject) {
        currentReject(new Error('Captcha cancelled by user'))
      }
    }

    const container = createOverlay(handleClose)

    // Add escape key listener
    document.addEventListener('keydown', handleEscapeKey)

    // Start timeout fail-safe
    timeoutId = setTimeout(() => {
      debug('[WAF Captcha] Timed out after 5 minutes')
      const currentReject = rejectFn
      cleanup()
      if (currentReject) {
        currentReject(new Error('Captcha timed out'))
      }
    }, WAF_CAPTCHA_TIMEOUT_MS)

    try {
      window.AwsWafCaptcha.renderCaptcha(container, {
        apiKey: config.wafCaptchaApiKey,
        onSuccess: () => {
          debug('[WAF Captcha] Successfully completed')
          cleanup()
          resolve()
        },
        onError: error => {
          debug('[WAF Captcha] SDK error:', error)
          cleanup()
          reject(error)
        },
      })
    } catch (error) {
      cleanup()
      reject(error)
    }
  })

  return captchaPromise
}

/**
 * Check if a captcha is currently being displayed
 * @returns {boolean}
 */
export function isCaptchaPending() {
  return captchaPromise !== null
}
